/**
 * CrewLinkNotificationContext - PRD-049
 *
 * Global context for CrewLink notifications that persists across page navigations.
 * Tracks:
 * - Sessions with unread messages
 * - User join notifications
 * - Global unread count for nav button glow
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { playNotificationSound, SoundType, initAudio } from '../utils/crewlinkAudio';

const CrewLinkNotificationContext = createContext(null);

// Local storage key for persisting state
const STORAGE_KEY = 'crewlink_notifications';

export const CrewLinkNotificationProvider = ({ children }) => {
    const { currentUser, supabase, isAuthenticated } = useAuth();
    const [unreadSessions, setUnreadSessions] = useState({}); // { sessionId: messageCount }
    const [userJoinNotifications, setUserJoinNotifications] = useState([]); // [{ sessionId, userId, userName, timestamp }]
    const [hasUnread, setHasUnread] = useState(false);
    const channelRef = useRef(null);
    const audioInitialized = useRef(false);

    // Initialize audio on first user interaction
    useEffect(() => {
        const handleInteraction = () => {
            if (!audioInitialized.current) {
                initAudio();
                audioInitialized.current = true;
            }
        };

        document.addEventListener('click', handleInteraction, { once: true });
        document.addEventListener('keydown', handleInteraction, { once: true });

        return () => {
            document.removeEventListener('click', handleInteraction);
            document.removeEventListener('keydown', handleInteraction);
        };
    }, []);

    // Load persisted state from localStorage
    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed.unreadSessions) {
                    setUnreadSessions(parsed.unreadSessions);
                }
                // User join notifications are ephemeral, don't persist
            }
        } catch (err) {
            console.log('[CrewLinkNotification] Error loading state:', err);
        }
    }, []);

    // Persist state to localStorage
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                unreadSessions,
                timestamp: Date.now()
            }));
        } catch (err) {
            console.log('[CrewLinkNotification] Error saving state:', err);
        }

        // Update hasUnread flag
        const totalUnread = Object.values(unreadSessions).reduce((sum, count) => sum + count, 0);
        setHasUnread(totalUnread > 0 || userJoinNotifications.length > 0);
    }, [unreadSessions, userJoinNotifications]);

    // Set up real-time subscriptions for messages and participant joins
    useEffect(() => {
        if (!isAuthenticated || !currentUser?.id) {
            return;
        }

        console.log('[CrewLinkNotification] Setting up global subscriptions for user:', currentUser.id);

        const channel = supabase
            .channel(`crewlink-global:${currentUser.id}`)
            // Listen for new messages in any session the user is part of
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'crewlink_session_messages'
                },
                async (payload) => {
                    const message = payload.new;
                    console.log('[CrewLinkNotification] Message received:', message.session_id, 'from:', message.user_id);

                    // Skip if the message is from the current user
                    // Note: column is 'user_id' not 'sender_id'
                    if (message.user_id === currentUser.id) {
                        console.log('[CrewLinkNotification] Skipping own message');
                        return;
                    }

                    // Check if user is a participant in this session
                    const { data: participation, error: partError } = await supabase
                        .from('crewlink_session_participants')
                        .select('id')
                        .eq('session_id', message.session_id)
                        .eq('user_id', currentUser.id)
                        .eq('is_active', true)
                        .maybeSingle();

                    if (partError) {
                        console.error('[CrewLinkNotification] Error checking participation:', partError);
                    }

                    // Also check if user is the host
                    const { data: sessionData, error: sessError } = await supabase
                        .from('crewlink_sessions')
                        .select('host_id')
                        .eq('id', message.session_id)
                        .single();

                    if (sessError) {
                        console.error('[CrewLinkNotification] Error checking host:', sessError);
                    }

                    const isParticipant = !!participation;
                    const isHost = sessionData?.host_id === currentUser.id;

                    console.log('[CrewLinkNotification] User role check:', { isParticipant, isHost, currentUserId: currentUser.id });

                    if (isParticipant || isHost) {
                        console.log('[CrewLinkNotification] Playing notification for session:', message.session_id);

                        // Play notification sound
                        playNotificationSound(SoundType.NEW_MESSAGE);

                        // Add to unread count for this session
                        setUnreadSessions(prev => ({
                            ...prev,
                            [message.session_id]: (prev[message.session_id] || 0) + 1
                        }));
                    } else {
                        console.log('[CrewLinkNotification] User not in session, skipping notification');
                    }
                }
            )
            // Listen for new participants joining sessions the user hosts or is part of
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'crewlink_session_participants'
                },
                async (payload) => {
                    const participant = payload.new;
                    console.log('[CrewLinkNotification] Participant join event:', participant.session_id, 'user:', participant.user_id);

                    // Skip if it's the current user joining
                    if (participant.user_id === currentUser.id) {
                        console.log('[CrewLinkNotification] Skipping - current user joined');
                        return;
                    }

                    // Check if current user is the host of this session
                    const { data: session, error: sessError } = await supabase
                        .from('crewlink_sessions')
                        .select('id, host_id, title')
                        .eq('id', participant.session_id)
                        .single();

                    if (sessError) {
                        console.error('[CrewLinkNotification] Error fetching session:', sessError);
                    }

                    // Also check if current user is a participant (to notify about new members)
                    const { data: participation, error: partError } = await supabase
                        .from('crewlink_session_participants')
                        .select('id')
                        .eq('session_id', participant.session_id)
                        .eq('user_id', currentUser.id)
                        .eq('is_active', true)
                        .maybeSingle();

                    if (partError) {
                        console.error('[CrewLinkNotification] Error checking participation:', partError);
                    }

                    const isHost = session?.host_id === currentUser.id;
                    const isParticipant = !!participation;

                    console.log('[CrewLinkNotification] User role for join notification:', { isHost, isParticipant });

                    if (isHost || isParticipant) {
                        // Fetch the new participant's profile
                        const { data: profile } = await supabase
                            .from('profiles')
                            .select('id, display_name, username')
                            .eq('id', participant.user_id)
                            .single();

                        const userName = profile?.display_name || profile?.username || 'Unknown User';

                        console.log('[CrewLinkNotification] User joined session:', userName, session?.id);

                        // Play user joined sound
                        playNotificationSound(SoundType.USER_JOINED);

                        // Add to join notifications
                        setUserJoinNotifications(prev => [
                            ...prev,
                            {
                                sessionId: participant.session_id,
                                sessionTitle: session?.title || 'Session',
                                userId: participant.user_id,
                                userName,
                                timestamp: Date.now()
                            }
                        ]);
                    } else {
                        console.log('[CrewLinkNotification] User not in session, skipping join notification');
                    }
                }
            )
            .subscribe((status) => {
                console.log('[CrewLinkNotification] Subscription status:', status);
            });

        channelRef.current = channel;

        return () => {
            console.log('[CrewLinkNotification] Cleaning up subscriptions');
            if (channelRef.current) {
                supabase.removeChannel(channelRef.current);
                channelRef.current = null;
            }
        };
    }, [isAuthenticated, currentUser?.id, supabase]);

    // Mark a session's messages as read
    const markSessionAsRead = useCallback((sessionId) => {
        setUnreadSessions(prev => {
            const updated = { ...prev };
            delete updated[sessionId];
            return updated;
        });
    }, []);

    // Clear a join notification
    const clearJoinNotification = useCallback((sessionId, userId) => {
        setUserJoinNotifications(prev =>
            prev.filter(n => !(n.sessionId === sessionId && n.userId === userId))
        );
    }, []);

    // Clear all join notifications for a session
    const clearSessionJoinNotifications = useCallback((sessionId) => {
        setUserJoinNotifications(prev =>
            prev.filter(n => n.sessionId !== sessionId)
        );
    }, []);

    // Clear all notifications
    const clearAllNotifications = useCallback(() => {
        setUnreadSessions({});
        setUserJoinNotifications([]);
    }, []);

    // Cleanup stale notifications - removes notifications for sessions not in the active list
    const cleanupStaleNotifications = useCallback((activeSessionIds) => {
        const activeSet = new Set(activeSessionIds);

        setUnreadSessions(prev => {
            const cleaned = {};
            Object.keys(prev).forEach(sessionId => {
                if (activeSet.has(sessionId)) {
                    cleaned[sessionId] = prev[sessionId];
                } else {
                    console.log('[CrewLinkNotification] Removing stale notification for session:', sessionId);
                }
            });
            return cleaned;
        });

        setUserJoinNotifications(prev =>
            prev.filter(n => {
                if (!activeSet.has(n.sessionId)) {
                    console.log('[CrewLinkNotification] Removing stale join notification for session:', n.sessionId);
                    return false;
                }
                return true;
            })
        );
    }, []);

    // Get unread count for a specific session
    const getSessionUnreadCount = useCallback((sessionId) => {
        return unreadSessions[sessionId] || 0;
    }, [unreadSessions]);

    // Get total unread count across all sessions
    const getTotalUnreadCount = useCallback(() => {
        return Object.values(unreadSessions).reduce((sum, count) => sum + count, 0);
    }, [unreadSessions]);

    // Get join notifications for a specific session
    const getSessionJoinNotifications = useCallback((sessionId) => {
        return userJoinNotifications.filter(n => n.sessionId === sessionId);
    }, [userJoinNotifications]);

    const value = {
        unreadSessions,
        userJoinNotifications,
        hasUnread,
        markSessionAsRead,
        clearJoinNotification,
        clearSessionJoinNotifications,
        clearAllNotifications,
        cleanupStaleNotifications,
        getSessionUnreadCount,
        getTotalUnreadCount,
        getSessionJoinNotifications,
        playNotificationSound,
        SoundType
    };

    return (
        <CrewLinkNotificationContext.Provider value={value}>
            {children}
        </CrewLinkNotificationContext.Provider>
    );
};

export const useCrewLinkNotifications = () => {
    const context = useContext(CrewLinkNotificationContext);
    if (!context) {
        throw new Error('useCrewLinkNotifications must be used within a CrewLinkNotificationProvider');
    }
    return context;
};

export default CrewLinkNotificationContext;
